/* ==========================================================================
   assets/hero-loader.js
   Drives the intro. It only ever adds/removes classes and seeds smoke
   elements — all timing and easing lives in hero.css.
   ========================================================================== */
(function () {
  'use strict';

  var SESSION_KEY = 'truspooky:intro-played';
  var hero = document.querySelector('[data-hero]');
  if (!hero) return;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var smokeBack = hero.querySelector('[data-smoke="back"]');
  var smokeFront = hero.querySelector('[data-smoke="front"]');
  var skipBtn = hero.querySelector('[data-intro-skip]');

  var rand = function (min, max) { return Math.random() * (max - min) + min; };
  var pick = function (arr) { return arr[Math.floor(Math.random() * arr.length)]; };

  /* Reads a --t-* value (declared in ms) off the root element. */
  function ms(name, fallback) {
    var raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    var n = parseFloat(raw);
    return isNaN(n) ? fallback : n;
  }

  /* ------------------------------------------------------------- smoke */
  function makePuff(opts) {
    var el = document.createElement('span');
    var tone = pick(opts.tones);
    el.className = 'puff puff--' + tone + (opts.ambient ? ' puff--ambient' : '');
    var size = rand(opts.size[0], opts.size[1]);
    el.style.setProperty('--x', rand(-6, 106) + '%');
    el.style.setProperty('--s', size + 'vmin');
    el.style.setProperty('--b', rand(opts.blur[0], opts.blur[1]) + 'px');
    el.style.setProperty('--rise', rand(opts.rise[0], opts.rise[1]) + 'vh');
    el.style.setProperty('--dx', rand(-14, 14) + 'vw');
    el.style.setProperty('--rot', rand(-70, 70) + 'deg');
    el.style.setProperty('--d', rand(opts.dur[0], opts.dur[1]) + 'ms');
    el.style.setProperty('--delay', rand(opts.delay[0], opts.delay[1]) + 'ms');
    el.style.setProperty('--o', rand(opts.opacity[0], opts.opacity[1]));
    return el;
  }

  function seed(layer, count, opts) {
    if (!layer) return;
    var frag = document.createDocumentFragment();
    for (var i = 0; i < count; i++) frag.appendChild(makePuff(opts));
    layer.appendChild(frag);
  }

  /* Tone mix follows the logo artwork: a purple billow carrying the wraith,
     with the orange kept low and small so it reads as embers at the base
     rather than a second colour competing with the lettering. */
  function seedIntroSmoke() {
    var total = parseInt(hero.dataset.puffCount, 10) || 34;
    /* Two things keep this from flaring into a flat purple screen: puffs are
       screen-blended so brightness ACCUMULATES where they overlap, and a
       narrow delay range makes them all peak at the same instant. Hence the
       low opacity ceiling and the wide, staggered delays. The short rise is
       deliberate too — smoke that travels the full viewport fills the frame
       and the black canvas stops reading. */
    seed(smokeBack, Math.round(total * 0.52), {
      tones: ['purple', 'purple', 'purple', 'soot', 'soot'],
      size: [26, 52], blur: [22, 46], rise: [26, 58],
      dur: [3200, 5200], delay: [0, 2200], opacity: [0.14, 0.30]
    });
    seed(smokeFront, Math.round(total * 0.30), {
      tones: ['soot', 'soot', 'soot', 'purple'],
      size: [40, 88], blur: [38, 70], rise: [34, 66],
      dur: [3600, 5600], delay: [0, 1800], opacity: [0.14, 0.30]
    });
    // Embers: short rise, tight blur, so they stay a glow along the floor.
    seed(smokeBack, Math.round(total * 0.18), {
      tones: ['orange'],
      size: [14, 34], blur: [16, 30], rise: [18, 46],
      dur: [2800, 4600], delay: [0, 1100], opacity: [0.30, 0.58]
    });
  }

  /* A slow drift that stays behind the wraith once the hero settles.
     ambient: true is what makes these loop — without it they play once and
     the hero goes static. */
  function seedAmbientSmoke() {
    seed(smokeBack, 8, {
      ambient: true,
      tones: ['purple', 'purple', 'soot'],
      size: [34, 70], blur: [40, 70], rise: [45, 80],
      dur: [11000, 17000], delay: [0, 9000], opacity: [0.10, 0.22]
    });
    seed(smokeBack, 3, {
      ambient: true,
      tones: ['orange'],
      size: [14, 30], blur: [18, 32], rise: [16, 38],
      dur: [9000, 14000], delay: [0, 7000], opacity: [0.14, 0.26]
    });
  }

  function clearSmoke() {
    [smokeBack, smokeFront].forEach(function (l) { if (l) l.innerHTML = ''; });
  }

  /* ---------------------------------------------------------- timeline */
  var finished = false;

  function finish(immediate) {
    if (finished) return;
    finished = true;

    var release = function () {
      hero.classList.remove('is-intro');
      document.documentElement.classList.remove('intro-active');
      hero.classList.add('hero--ready');
      clearSmoke();
      if (!reduced) seedAmbientSmoke();
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
      document.dispatchEvent(new CustomEvent('truspooky:introdone'));
    };

    if (immediate) return release();

    hero.classList.add('is-outro');
    window.setTimeout(function () {
      release();
      hero.classList.remove('is-outro');
    }, 700);
  }

  function play() {
    document.documentElement.classList.add('intro-active');
    hero.classList.add('is-intro');
    window.scrollTo(0, 0);
    seedIntroSmoke();

    var exitAt = ms('--t-exit', 3300);
    window.setTimeout(function () { finish(false); }, exitAt);
    // Failsafe: never trap someone behind the intro if an animation stalls.
    window.setTimeout(function () { finish(true); }, exitAt + 4000);
  }

  function shouldPlay() {
    if (hero.dataset.intro !== 'true') return false;
    if (reduced) return false;
    if (hero.dataset.introOnce === 'true') {
      try { if (sessionStorage.getItem(SESSION_KEY)) return false; } catch (e) {}
    }
    return true;
  }

  if (skipBtn) {
    skipBtn.addEventListener('click', function () { finish(false); });
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && hero.classList.contains('is-intro')) finish(false);
  });

  if (shouldPlay()) {
    play();
  } else {
    hero.classList.add('hero--ready');
    finished = true;
    if (!reduced) seedAmbientSmoke();
    document.dispatchEvent(new CustomEvent('truspooky:introdone'));
  }
})();
